/**
 * Dexie / IndexedDB — Phase 1 (ADR-0002).
 * Eine Tabelle `kv_items` spiegelt die frühere localStorage-KV-Schicht.
 */

import Dexie from 'dexie';

export const DB_NAME = 'elkheir_v1';
export const META_IDB_MIGRATION_KEY = 'Elkheir:v1:meta:idb_kv_migration_v1';

export class ElkheirDatabase extends Dexie {
  constructor() {
    super(DB_NAME);
    this.version(1).stores({
      kv_items: 'key',
      meta: 'key'
    });
    // Phase 1b: Outbox für Offline-Sync (ADR-0005)
    this.version(2).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]'
    });
    // Phase 5: Shadow-Index pro Aggregat (sales, purchases, …)
    this.version(3).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]',
      domain_aggregate:
        'id, businessId, aggregate, entityId, [businessId+aggregate], updatedAt'
    });
    // Phase 13: Getrennte Entity-Stores (sales, purchases, lots, products, customers)
    this.version(4).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]',
      domain_aggregate:
        'id, businessId, aggregate, entityId, [businessId+aggregate], updatedAt',
      entity_sales: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_purchases:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_lots: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customers:
        'id, businessId, entityId, [businessId+entityId], updatedAt'
    });
    // Phase 16: suppliers + stockData
    this.version(5).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]',
      domain_aggregate:
        'id, businessId, aggregate, entityId, [businessId+aggregate], updatedAt',
      entity_sales: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_purchases:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_lots: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_suppliers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_data:
        'id, businessId, entityId, [businessId+entityId], updatedAt'
    });
    // Phase 17: invoices + orders + expenses
    this.version(6).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]',
      domain_aggregate:
        'id, businessId, aggregate, entityId, [businessId+aggregate], updatedAt',
      entity_sales: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_purchases:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_lots: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_suppliers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_data:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_orders: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_expenses:
        'id, businessId, entityId, [businessId+entityId], updatedAt'
    });
    // Phase 18: payments + debts
    this.version(7).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]',
      domain_aggregate:
        'id, businessId, aggregate, entityId, [businessId+aggregate], updatedAt',
      entity_sales: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_purchases:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_lots: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_suppliers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_data:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_orders: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_expenses:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoice_payments:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supplier_debts:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customer_debts:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customer_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supermarket_payments:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supermarket_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt'
    });
    // Phase 19: expiredProducts + notifications + workerSessions + reports + discrepancyManagement
    this.version(8).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]',
      domain_aggregate:
        'id, businessId, aggregate, entityId, [businessId+aggregate], updatedAt',
      entity_sales: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_purchases:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_lots: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_suppliers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_data:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_orders: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_expenses:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoice_payments:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supplier_debts:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customer_debts:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customer_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supermarket_payments:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supermarket_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_expired_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_notifications:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_worker_sessions:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_reports: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_discrepancy_management:
        'id, businessId, entityId, [businessId+entityId], updatedAt'
    });
    // Phase 20: Blob-Hülle (stockLocations, metadata, …)
    this.version(9).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]',
      domain_aggregate:
        'id, businessId, aggregate, entityId, [businessId+aggregate], updatedAt',
      entity_sales: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_purchases:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_lots: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_suppliers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_data:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_orders: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_expenses:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoice_payments:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supplier_debts:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customer_debts:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customer_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supermarket_payments:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supermarket_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_expired_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_notifications:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_worker_sessions:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_reports: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_discrepancy_management:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_locations:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_persistent_stock_data:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_offline_queue:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_business_info:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_user_info: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_metadata: 'id, businessId, entityId, [businessId+entityId], updatedAt'
    });
    // Phase 22: stockMovements-Ledger
    this.version(10).stores({
      kv_items: 'key',
      meta: 'key',
      outbox: 'id, status, createdAt, entityType, [status+createdAt]',
      domain_aggregate:
        'id, businessId, aggregate, entityId, [businessId+aggregate], updatedAt',
      entity_sales: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_purchases:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_lots: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_suppliers:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_data:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_orders: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_expenses:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_invoice_payments:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supplier_debts:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customer_debts:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_customer_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supermarket_payments:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_supermarket_invoices:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_expired_products:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_notifications:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_worker_sessions:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_reports: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_discrepancy_management:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_locations:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_persistent_stock_data:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_offline_queue:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_business_info:
        'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_user_info: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_metadata: 'id, businessId, entityId, [businessId+entityId], updatedAt',
      entity_stock_movements:
        'id, businessId, entityId, [businessId+entityId], updatedAt'
    });
  }
}

/** @typedef {'pending'|'processing'|'done'|'failed'} OutboxStatus */

/**
 * @typedef {Object} OutboxEntry
 * @property {string} id — clientMutationId
 * @property {string} entityType — z. B. report_sync
 * @property {unknown} payload
 * @property {OutboxStatus} status
 * @property {string} createdAt
 * @property {number} attempts
 * @property {string|null} [lastError]
 * @property {string|null} [processedAt]
 */

let dbInstance = null;

export function getElkheirDb() {
  if (!dbInstance) {
    dbInstance = new ElkheirDatabase();
  }
  return dbInstance;
}

export function __resetElkheirDbForTests() {
  dbInstance = null;
}
